import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, OTP_STATE } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail, Lock, Loader2, ArrowLeft } from 'lucide-react';

// Country codes for phone input
const COUNTRY_CODES = [
  { code: '963', country: 'Syria', flag: '🇸🇾' },
  { code: '966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '971', country: 'UAE', flag: '🇦🇪' },
  { code: '962', country: 'Jordan', flag: '🇯🇴' },
  { code: '961', country: 'Lebanon', flag: '🇱🇧' },
  { code: '20', country: 'Egypt', flag: '🇪🇬' },
  { code: '964', country: 'Iraq', flag: '🇮🇶' },
  { code: '1', country: 'USA', flag: '🇺🇸' },
];

const normalizePhoneInput = (value) =>
  String(value || '')
    .replace(/\D/g, '')
    .replace(/^0+/, '');

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const { login, verifyOtp, resendOtp, cancelOtpVerification, otpState, pendingAuth } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('patient');
  const [useEmail, setUseEmail] = useState(false);
  const [countryCode, setCountryCode] = useState('963');
  const [otpCode, setOtpCode] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [resending, setResending] = useState(false);
  
  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    password: '',
    rememberMe: false,
  });

  // OTP countdown timer
  useEffect(() => {
    let interval;
    if (otpState !== OTP_STATE.NONE && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpState, otpTimer]);

  // Start timer when OTP is needed
  useEffect(() => {
    if (otpState !== OTP_STATE.NONE) {
      setOtpTimer(120); // 2 minutes
    }
  }, [otpState]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePhoneChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      phone: normalizePhoneInput(value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const phoneNumber = normalizePhoneInput(formData.phone);
      const phoneOrEmail = useEmail 
        ? formData.email 
        : `+${countryCode}${phoneNumber}`;
      
      const result = await login(phoneOrEmail, formData.password, activeTab);
      
      if (result.success) {
        const destinationType = result.userType || activeTab;
        toast.success(t('common.success'), {
          description: result.isDemo 
            ? (lang === 'ar' ? 'مرحباً بك! (وضع تجريبي)' : 'Welcome! (Demo mode)')
            : (lang === 'ar' ? 'مرحباً بعودتك!' : 'Welcome back!'),
        });
        navigate(destinationType === 'doctor' ? '/doctor/dashboard' : '/appointments');
      } else if (result.needsOtp) {
        toast.info(lang === 'ar' ? 'التحقق مطلوب' : 'Verification Required', {
          description: lang === 'ar' 
            ? 'تم إرسال رمز التحقق إلى هاتفك/بريدك الإلكتروني'
            : 'OTP code has been sent to your phone/email',
        });
      } else {
        toast.error(t('common.error'), {
          description: result.error || 'Login failed',
        });
      }
    } catch (error) {
      toast.error(t('common.error'), {
        description: lang === 'ar' ? 'حدث خطأ غير متوقع' : 'An unexpected error occurred',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length < 4) {
      toast.error(t('common.error'), {
        description: lang === 'ar' ? 'الرجاء إدخال رمز صحيح' : 'Please enter a valid OTP code',
      });
      return;
    }

    setLoading(true);
    try {
      const result = await verifyOtp(otpCode);
      
      if (result.success) {
        const destinationType = result.userType || pendingAuth?.userType || activeTab;
        toast.success(t('common.success'), {
          description: lang === 'ar' ? 'تم التحقق بنجاح!' : 'Verification successful!',
        });
        if (destinationType === 'doctor') {
          navigate('/doctor/dashboard');
        } else {
          navigate(result.isNewUser ? '/profile' : '/appointments');
        }
      } else {
        toast.error(t('common.error'), {
          description: result.error || (lang === 'ar' ? 'رمز خاطئ' : 'Wrong OTP code'),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      const result = await resendOtp();
      if (result.success) {
        setOtpTimer(120);
        toast.success(t('common.success'), {
          description: lang === 'ar' ? 'تم إعادة إرسال الرمز' : 'OTP code resent',
        });
      } else {
        toast.error(t('common.error'), {
          description: result.error,
        });
      }
    } finally {
      setResending(false);
    }
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // OTP Verification View
  if (otpState !== OTP_STATE.NONE) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center p-4" data-testid="otp-page">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <img src="/images/logo.png" alt="Dactrha" className="h-12 w-12 rounded-lg object-contain" />
              <span className="text-2xl font-bold text-slate-900 dark:text-white">{t('common.appName')}</span>
            </Link>
          </div>

          <Card className="dark:bg-slate-800 border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <Button 
                variant="ghost" 
                size="sm" 
                className="absolute left-4 top-4"
                onClick={cancelOtpVerification}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t('common.back')}
              </Button>
              <CardTitle className="text-2xl">
                {lang === 'ar' ? 'التحقق من الهاتف' : 'Phone Verification'}
              </CardTitle>
              <CardDescription>
                {lang === 'ar' 
                  ? 'مرحباً بك في تطبيق دكترها'
                  : 'Welcome to Dactrha application'
                }
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-4 space-y-6">
              <p className="text-center text-slate-600 dark:text-slate-400">
                {countryCode === '963' && formData.phone
                  ? (lang === 'ar' 
                      ? 'تم إرسال رمز التحقق إلى هاتفك، يرجى التحقق وإدخاله أدناه'
                      : 'We sent the OTP code to your phone, please check it and enter below')
                  : (lang === 'ar'
                      ? 'تم إرسال رمز التحقق إلى بريدك الإلكتروني، يرجى التحقق وإدخاله أدناه'
                      : 'We sent the OTP code to your email, please check it and enter below')
                }
              </p>

              <div className="space-y-2">
                <Label>{lang === 'ar' ? 'رمز التحقق' : 'OTP Code'}</Label>
                <Input
                  type="text"
                  placeholder="- - - - - -"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  maxLength={6}
                  data-testid="otp-input"
                />
              </div>

              <Button 
                className="w-full bg-teal-600 hover:bg-teal-700" 
                onClick={handleVerifyOtp}
                disabled={loading || otpCode.length < 4}
                data-testid="verify-otp-button"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('common.loading')}</>
                ) : (
                  lang === 'ar' ? 'تحقق' : 'Verify'
                )}
              </Button>

              <div className="text-center">
                {otpTimer > 0 ? (
                  <p className="text-slate-500 dark:text-slate-400">{formatTimer(otpTimer)}</p>
                ) : resending ? (
                  <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                ) : (
                  <Button variant="link" onClick={handleResendOtp} className="text-teal-600">
                    {lang === 'ar' ? 'إعادة إرسال رمز التحقق' : 'Resend the OTP Code Again'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Login Form View
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/images/logo.png" alt="Dactrha" className="h-12 w-12 rounded-lg object-contain" />
            <span className="text-2xl font-bold text-slate-900 dark:text-white">{t('common.appName')}</span>
          </Link>
        </div>

        <Card className="dark:bg-slate-800 border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl">{t('auth.loginTitle')}</CardTitle>
            <CardDescription>{t('auth.loginSubtitle')}</CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="patient" data-testid="patient-tab">
                  {t('auth.loginAsPatient')}
                </TabsTrigger>
                <TabsTrigger value="doctor" data-testid="doctor-tab">
                  {t('auth.loginAsDoctor')}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Toggle between phone and email */}
              <div className="flex justify-end">
                <Button 
                  type="button" 
                  variant="link" 
                  size="sm"
                  onClick={() => setUseEmail(!useEmail)}
                  className="text-teal-600 p-0 h-auto"
                >
                  {useEmail 
                    ? (lang === 'ar' ? 'استخدم رقم الهاتف' : 'Use phone number')
                    : (lang === 'ar' ? 'استخدم البريد الإلكتروني' : 'Use email instead')
                  }
                </Button>
              </div>

              {useEmail ? (
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="phone">{lang === 'ar' ? 'رقم الهاتف' : 'Phone Number'}</Label>
                  <div className="flex gap-2">
                    <Select value={countryCode} onValueChange={setCountryCode}>
                      <SelectTrigger className="w-[110px]" data-testid="country-code-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRY_CODES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.flag} +{c.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder={lang === 'ar' ? 'أدخل رقمك' : 'Enter your number'}
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        className="pl-10"
                        autoComplete="off"
                        required
                        data-testid="phone-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <Link to="/forgot-password" className="text-sm text-teal-600 hover:underline">
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberMe"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, rememberMe: checked }))}
                  data-testid="remember-checkbox"
                />
                <Label htmlFor="rememberMe" className="text-sm font-normal cursor-pointer">
                  {t('auth.rememberMe')}
                </Label>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-teal-600 hover:bg-teal-700" 
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('common.loading')}</>
                ) : (
                  t('common.login')
                )}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="flex flex-col gap-4 pt-0">
            <div className="relative w-full">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500 dark:text-slate-400">
                  {lang === 'ar' ? 'أو' : 'or'}
                </span>
              </div>
            </div>
            
            <p className="text-center text-sm text-slate-600 dark:text-slate-400">
              {t('auth.noAccount')}{' '}
              <Link to="/register" className="font-medium text-teal-600 hover:underline" data-testid="register-link">
                {t('common.register')}
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
